import { type FormEvent, useEffect, useState } from 'react';
import type { AccountSetupPayload } from '@livechat/shared';

import { completeAccountSetup, previewAccountSetup } from '../api.js';

interface AccountSetupProps {
	token: string;
	onComplete: () => void;
}

export function AccountSetup({ token, onComplete }: AccountSetupProps) {
	const [setup, setSetup] = useState<AccountSetupPayload>();
	const [error, setError] = useState<string>();
	const [busy, setBusy] = useState(false);

	useEffect(() => {
		void previewAccountSetup(token)
			.then(setSetup)
			.catch((caught: unknown) =>
				setError(
					caught instanceof Error ? caught.message : 'This setup link is unavailable',
				),
			);
	}, [token]);

	async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
		event.preventDefault();
		setBusy(true);
		setError(undefined);
		const data = new FormData(event.currentTarget);
		try {
			await completeAccountSetup({
				displayName: String(data.get('displayName')),
				password: String(data.get('password')),
				token,
			});
			onComplete();
		} catch (caught) {
			setError(caught instanceof Error ? caught.message : 'Account setup failed');
		} finally {
			setBusy(false);
		}
	}

	return (
		<div className="centred-page">
			<form className="auth-card setup-card" onSubmit={(event) => void submit(event)}>
				<p className="eyebrow">Account invitation</p>
				<h2>{setup ? `Join ${setup.tenantName}` : 'Checking your invitation…'}</h2>
				{setup && <p className="muted">Finish setting up the account for {setup.email}.</p>}
				{error && <div className="alert error">{error}</div>}
				{setup && (
					<>
						<label>
							<span>Your name</span>
							<input
								name="displayName"
								required
								maxLength={160}
								autoComplete="name"
							/>
						</label>
						<label>
							<span>Create a password</span>
							<input
								name="password"
								type="password"
								required
								minLength={12}
								maxLength={128}
								autoComplete="new-password"
							/>
						</label>
						<button className="primary-button full-button" disabled={busy}>
							{busy ? 'Creating account…' : 'Create account'}
						</button>
					</>
				)}
			</form>
		</div>
	);
}
